import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, AlertTriangle, TrendingUp, Building2, HelpCircle } from "lucide-react";
import axios from "axios";
import { SeoHead } from "@/lib/seo";
import { CITIES, CITY_SLUGS } from "@/lib/seo-cities";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-template-fits-all city landing page.
 *
 * Why a single component: all five cities share the same content shape
 * (hero / OMI table / sub-markets / constraints / investment angle /
 * examples / FAQ). Static copy comes from `lib/seo-cities.js`; dynamic
 * bits (OMI numbers + real example analyses for that town) come from
 * `GET /api/seo/city/:slug`.
 */
const CityPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug || !CITY_SLUGS.includes(slug)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/seo/city/${slug}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status || "error");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (!slug || !CITY_SLUGS.includes(slug)) {
    return <Navigate to="/" replace />;
  }

  const city = CITIES[slug];
  const omi = data?.omi;
  const examples = data?.examples || [];

  // FAQPage schema so Google can render rich FAQ snippets in search results.
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: city.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const placeJsonLd = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: city.name,
    address: {
      "@type": "PostalAddress",
      addressLocality: city.name,
      addressRegion: city.province,
      addressCountry: "IT",
    },
  };

  const breadcrumbItems = [
    { label: "Home", to: "/" },
    { label: "Città", to: "/" },
    { label: city.name },
  ];
  const breadcrumbJsonLdObj = breadcrumbJsonLd(breadcrumbItems);

  return (
    <main data-testid={`city-page-${slug}`} className="bg-tufo">
      <SeoHead
        title={city.metaTitle}
        description={city.metaDesc}
        path={`/citta/${slug}`}
        image={`https://dealrating.it/api/og/city/${slug}.png`}
        jsonLd={[placeJsonLd, faqJsonLd, breadcrumbJsonLdObj]}
      />

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-10 md:pb-12">
        <Breadcrumb items={breadcrumbItems} />
        <div className="label-eyebrow mb-3 mt-6">Mercato immobiliare · {city.province}</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#282C2A] leading-[1.05] tracking-tight">
          Comprare casa a {city.name}
        </h1>
        <div className="mt-6 md:mt-8 space-y-4 max-w-3xl text-sm md:text-base leading-relaxed text-[#282C2A]">
          {city.intro.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      {/* OMI quick stats */}
      {omi && (
        <section className="max-w-5xl mx-auto px-5 md:px-10 pb-12">
          <div className="grid md:grid-cols-3 gap-px bg-[#E6E4DD] border-2 border-[#282C2A]">
            <div className="bg-white p-6">
              <div className="label-eyebrow">Residenziale</div>
              <div className="font-serif-display text-3xl mt-2 text-[#282C2A]">
                € {omi.residential_eur_sqm.toLocaleString("it-IT")}
                <span className="font-mono text-xs ml-2 text-[#6C706D]">/m²</span>
              </div>
              <div className="text-xs text-[#6C706D] mt-1">Media OMI {city.name}</div>
            </div>
            <div className="bg-white p-6">
              <div className="label-eyebrow">Agricolo / terreni</div>
              <div className="font-serif-display text-3xl mt-2 text-[#282C2A]">
                € {omi.agricultural_eur_sqm.toLocaleString("it-IT")}
                <span className="font-mono text-xs ml-2 text-[#6C706D]">/m²</span>
              </div>
              <div className="text-xs text-[#6C706D] mt-1">Stima OMI base</div>
            </div>
            <div className="bg-[#4A5D23] text-white p-6 flex flex-col">
              <div className="label-eyebrow text-white/80">Analizza un annuncio</div>
              <p className="text-sm mt-2 leading-relaxed">
                Incolla l'URL di un annuncio a {city.name} e ottieni un punteggio 0-10 in 20 secondi.
              </p>
              <Link
                to="/"
                data-testid="city-cta"
                className="mt-auto pt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold hover:underline"
              >
                Inizia gratis <ArrowRight size={13} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Sub-markets */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 pb-12">
        <div className="flex items-center gap-2 label-eyebrow mb-4">
          <Building2 size={12} strokeWidth={1.75} /> Zone e sub-mercati
        </div>
        <h2 className="font-serif-display text-3xl text-[#282C2A] mb-6">
          Le zone di {city.name}
        </h2>
        <ul className="space-y-3">
          {city.markets.map((m, i) => (
            <li key={i} className="flex gap-3 text-sm text-[#282C2A] leading-relaxed">
              <MapPin size={14} strokeWidth={1.5} className="text-[#4A5D23] mt-1 shrink-0" />
              <span>{m}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Constraints */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 pb-12">
        <div className="flex items-center gap-2 label-eyebrow mb-4">
          <AlertTriangle size={12} strokeWidth={1.75} /> Vincoli e particolarità
        </div>
        <h2 className="font-serif-display text-3xl text-[#282C2A] mb-6">
          Cosa devi sapere prima di comprare a {city.name}
        </h2>
        <ul className="space-y-3 border-l-2 border-[#A0522D] pl-5">
          {city.constraints.map((c, i) => (
            <li key={i} className="text-sm text-[#282C2A] leading-relaxed">{c}</li>
          ))}
        </ul>
      </section>

      {/* Investment angle */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 pb-12">
        <div className="flex items-center gap-2 label-eyebrow mb-4">
          <TrendingUp size={12} strokeWidth={1.75} /> Investimento
        </div>
        <h2 className="font-serif-display text-3xl text-[#282C2A] mb-4">
          La tesi di investimento
        </h2>
        <p className="text-base leading-relaxed text-[#282C2A] max-w-3xl">
          {city.investAngle}
        </p>
      </section>

      {/* Real examples */}
      {examples.length > 0 && (
        <section className="max-w-5xl mx-auto px-5 md:px-10 pb-12">
          <div className="label-eyebrow mb-4">Esempi reali</div>
          <h2 className="font-serif-display text-3xl text-[#282C2A] mb-6">
            Annunci recenti a {city.name}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {examples.slice(0, 3).map((ex) => (
              <Link
                key={ex.id}
                to={`/sheet/${ex.id}`}
                data-testid={`city-example-${ex.id}`}
                className="border border-[#E6E4DD] bg-white p-5 hover:border-[#282C2A] transition-colors group"
              >
                <div className="flex items-baseline justify-between">
                  <div className="label-eyebrow">{ex.status}</div>
                  <div className="font-serif-display text-2xl text-[#282C2A]">
                    {ex.total_score}<span className="text-xs text-[#6C706D]">/10</span>
                  </div>
                </div>
                <div className="text-sm text-[#282C2A] mt-3 leading-snug line-clamp-3">
                  {ex.title || ex.technical_note}
                </div>
                {ex.asking_price && (
                  <div className="text-xs font-mono uppercase tracking-widest text-[#6C706D] mt-3">
                    € {ex.asking_price.toLocaleString("it-IT")}
                    {ex.surface_sqm && ` · ${ex.surface_sqm} m²`}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-5 md:px-10 pb-20">
        <div className="flex items-center gap-2 label-eyebrow mb-4">
          <HelpCircle size={12} strokeWidth={1.75} /> Domande frequenti
        </div>
        <h2 className="font-serif-display text-3xl text-[#282C2A] mb-6">
          FAQ — comprare casa a {city.name}
        </h2>
        <div className="space-y-6">
          {city.faq.map((item, i) => (
            <div key={i} data-testid={`city-faq-${i}`}>
              <h3 className="font-serif-display text-xl text-[#282C2A]">{item.q}</h3>
              <p className="text-sm text-[#282C2A] mt-2 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {error && (
        <div className="max-w-5xl mx-auto px-5 md:px-10 pb-10 text-xs font-mono text-[#A0522D]">
          Could not load live data: {error}
        </div>
      )}
    </main>
  );
};

export default CityPage;
